<template>
  <div class="evidences-page">
    <!-- Navbar -->
    <nav class="evidences-navbar">
      <img :src="logo" alt="Logo" class="navbar-logo" />
    </nav>

    <div class="evidences-container">
      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('evidences.loading') }}</p>
      </div>

      <!-- Enlace expirado -->
      <div v-else-if="isExpired" class="expired-state">
        <i class="material-icons expired-icon">link_off</i>
        <h2>{{ $t('evidences.linkExpired') }}</h2>
        <p>{{ error }}</p>
        <p class="expired-hint"><i class="material-icons hint-icon">info</i> {{ $t('evidences.linkExpiredHint') }}</p>
      </div>

      <!-- Error genérico -->
      <div v-else-if="error" class="error-state">
        <i class="material-icons error-icon">error_outline</i>
        <h2>{{ $t('evidences.error') }}</h2>
        <p>{{ error }}</p>
      </div>

      <!-- Contenido del servicio -->
      <div v-else class="service-content">
        <header class="service-header">
          <h1>
            <i class="material-icons header-icon">{{ hasExistingEvidences ? 'photo_library' : 'cloud_upload' }}</i>
            {{ hasExistingEvidences ? $t('evidences.title') : $t('evidences.uploadTitle') }}
          </h1>
          <span class="status-badge" :class="service.status">{{ statusLabel }}</span>
        </header>

        <!-- Mensaje de evidencias ya cargadas -->
        <section v-if="hasExistingEvidences" class="success-notice mb-3">
          <i class="material-icons success-icon">check_circle</i>
          <p>{{ $t('evidences.alreadyUploaded') }}</p>
        </section>

        <div class="row">
          <!-- Primera columna: Imágenes (abajo en móvil) -->
          <div class="col-12 col-md-7 order-2 order-md-1">
            <section class="evidences-upload">
              <!-- Aviso importante -->
              <div v-if="!hasExistingEvidences" class="warning-notice">
                <i class="material-icons warning-icon">warning</i>
                <div class="warning-content">
                  <strong>{{ $t('evidences.important') }}</strong> {{ $t('evidences.warningMessage') }} <strong>{{ $t('evidences.notProvided') }}</strong>. {{ $t('evidences.deadline') }} <strong>{{ expireAtFormatted }}</strong>
                </div>
              </div>

              <div class="images-grid">
                <div
                  v-for="(image, index) in images"
                  :key="index"
                  class="image-upload-card"
                  :class="{ 'has-image': image.preview, 'uploading': image.uploading, 'readonly': image.uploaded }"
                >
                  <input
                    v-if="!image.uploaded"
                    type="file"
                    :id="'image-' + index"
                    :ref="'fileInput' + index"
                    accept="image/*"
                    @change="handleImageSelect($event, index)"
                    hidden
                  />

                  <div v-if="image.preview" class="image-preview">
                    <img :src="image.preview" :alt="'Evidencia ' + (index + 1)" />
                    <button
                      v-if="!image.uploaded"
                      class="remove-btn"
                      @click="removeImage(index)"
                      :disabled="image.uploading"
                    >
                      <i class="material-icons">close</i>
                    </button>
                    <div v-if="image.uploading" class="upload-overlay">
                      <div class="spinner small"></div>
                    </div>
                    <div v-if="image.uploaded" class="evidence-label">{{ evidencesNameImages[index].name }}</div>
                  </div>

                  <label v-else-if="!image.uploaded" :for="'image-' + index" class="upload-placeholder text-center">
                    <i class="material-icons camera-icon">add_a_photo</i>
                    <span>{{ evidencesNameImages[index].text }}</span>
                  </label>

                  <div v-else class="upload-placeholder text-center readonly">
                    <i class="material-icons camera-icon">photo_camera</i>
                    <span>{{ evidencesNameImages[index].text }}</span>
                  </div>
                </div>
              </div>

              <!-- Botón de envío -->
              <section v-if="!hasExistingEvidences" class="submit-section">
                <button
                  class="submit-btn"
                  :disabled="!canSubmit || submitting"
                  @click="submitEvidences"
                >
                  <i v-if="submitting" class="material-icons btn-icon spinning">sync</i>
                  <i v-else class="material-icons btn-icon">send</i>
                  <span v-if="submitting">{{ $t('evidences.sending') }}</span>
                  <span v-else>{{ $t('evidences.submitEvidences') }}</span>
                </button>
                <p v-if="selectedCount > 0" class="upload-progress">
                  <i class="material-icons progress-icon">photo_library</i>
                  {{ $t('evidences.imagesSelected', { count: selectedCount }) }}
                </p>
              </section>
            </section>
          </div>

          <!-- Segunda columna: Datos del servicio (arriba en móvil) -->
          <div class="col-12 col-md-5 order-1 order-md-2">
            <!-- Información del servicio -->
            <section class="service-info">
              <div class="info-card">
                <div class="info-row highlight">
                  <span class="label"><i class="material-icons label-icon">person</i> {{ $t('evidences.client') }}</span>
                  <span class="value">{{ service.clientName }}</span>
                </div>

                <div class="info-row">
                  <span class="label"><i class="material-icons label-icon">trip_origin</i> {{ $t('evidences.pickup') }}</span>
                  <span class="value">{{ service.pickup }}</span>
                </div>

                <div class="info-row">
                  <span class="label"><i class="material-icons label-icon">place</i> {{ $t('evidences.dropoff') }}</span>
                  <span class="value">{{ service.dropoff }}</span>
                </div>

                <div class="info-row highlight">
                  <span class="label"><i class="material-icons label-icon">schedule</i> {{ $t('evidences.dateTime') }}</span>
                  <span class="value">{{ service.pickupDatetime }}</span>
                </div>

                <div class="info-grid">
                  <div class="info-item">
                    <span class="label"><i class="material-icons label-icon">local_taxi</i> {{ $t('evidences.type') }}</span>
                    <span class="value small">{{ service.serviceType }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label"><i class="material-icons label-icon">swap_horiz</i> {{ $t('evidences.trip') }}</span>
                    <span class="value">{{ tripLabel }}</span>
                  </div>
                </div>

                <div class="info-grid">
                  <div class="info-item">
                    <span class="label"><i class="material-icons label-icon">group</i> {{ $t('evidences.passengers') }}</span>
                    <span class="value">{{ passengersText }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label"><i class="material-icons label-icon">flight</i> {{ $t('evidences.flight') }}</span>
                    <span class="value">{{ service.flightNumber || 'N/A' }}</span>
                  </div>
                </div>

                <div class="info-row">
                  <span class="label"><i class="material-icons label-icon">storefront</i> {{ $t('evidences.channel') }}</span>
                  <span class="value small">{{ service.salesChannel }} - {{ service.salesChannelReference }}</span>
                </div>
              </div>
            </section>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import logo from "@/assets/logo-sc-azul.png";

export default {
  name: 'Evidences',
  data() {
    return {
      logo,
      loading: true,
      error: null,
      isExpired: false,
      expireAt: null,
      token: null,
      service: null,
      images: [
        { file: null, preview: null, uploading: false, uploaded: false },
        { file: null, preview: null, uploading: false, uploaded: false },
        { file: null, preview: null, uploading: false, uploaded: false },
        { file: null, preview: null, uploading: false, uploaded: false }
      ],
      submitting: false,
      hasExistingEvidences: false
    };
  },
  computed: {
    evidencesNameImages() {
      return [
        { name: "gps1", text: this.$t('evidences.imageNames.gps1') },
        { name: "gps2", text: this.$t('evidences.imageNames.gps2') },
        { name: "photo1", text: this.$t('evidences.imageNames.photo1') },
        { name: "photo2", text: this.$t('evidences.imageNames.photo2') }
      ];
    },
    statusLabel() {
      const status = this.service?.status;
      const key = `evidences.statusLabels.${status}`;
      const translated = this.$t(key);
      return translated !== key ? translated : status;
    },
    tripLabel() {
      const trip = this.service?.trip;
      const key = `evidences.tripLabels.${trip}`;
      const translated = this.$t(key);
      return translated !== key ? translated : trip;
    },
    passengersText() {
      if (!this.service) return '';
      const parts = [];
      if (this.service.adults) parts.push(`${this.service.adults} Ad`);
      if (this.service.children) parts.push(`${this.service.children} Ni`);
      if (this.service.infants) parts.push(`${this.service.infants} Inf`);
      return parts.join(', ');
    },
    selectedCount() {
      return this.images.filter(img => img.file !== null).length;
    },
    canSubmit() {
      return this.images.every(img => img.file !== null || img.uploaded);
    },
    expireAtFormatted() {
      if (!this.expireAt) return '';
      const date = new Date(this.expireAt);
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },
  async mounted() {
    this.token = this.$route.query.token;
    await this.verifyLink(this.token);
  },
  methods: {
    async verifyLink(token) {
      try {
        const response = await this.$root.api.post('/supplier-evidences', { token });
        if (response.data.status === 'success') {
          this.service = response.data.data;
          this.expireAt = response.data.expireAt;

          // Precargar cualquier evidencia ya subida, aunque el proceso no esté completo
          if (this.service.evidenceFiles && this.service.evidenceFiles.length > 0) {
            this.loadExistingEvidences(this.service.evidenceFiles);
          }
          // Solo se bloquea la pantalla como solo-lectura cuando ya están las 4 evidencias
          this.hasExistingEvidences = (this.service.evidenceFiles?.length || 0) >= this.images.length;
        } else {
          this.error = response.data.error || 'Error al cargar el servicio';
        }
      } catch (err) {
        if (err.response?.status === 410 && err.response.data.status === 'expired') {
          this.isExpired = true;
          this.error = err.response.data.message;
        } else {
          this.error = err.response?.data?.message || 'Error al cargar página';
        }
        console.error('ERROR LINK VALIDATION', err.response?.data);
      } finally {
        this.loading = false;
      }
    },

    async handleImageSelect(event, index) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // Comprimir imagen si es mayor a 1080p
        const compressedFile = await this.compressImage(file);

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.$set(this.images, index, {
            ...this.images[index],
            preview: e.target.result,
            file: compressedFile
          });
        };
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error('Error processing image', err);
        // Si falla la compresión, usar archivo original
        const reader = new FileReader();
        reader.onload = (e) => {
          this.$set(this.images, index, {
            ...this.images[index],
            preview: e.target.result,
            file: file
          });
        };
        reader.readAsDataURL(file);
      }
    },

    compressImage(file, maxSize = 1080, quality = 0.8) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;

          // Solo comprimir si es mayor a maxSize
          if (width <= maxSize && height <= maxSize) {
            resolve(file);
            return;
          }

          // Calcular nuevas dimensiones manteniendo proporción
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }

          // Crear canvas y redimensionar
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir a blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            quality
          );
        };

        img.onerror = () => reject(new Error('Error loading image'));
        img.src = URL.createObjectURL(file);
      });
    },

    async uploadImage(index) {
      const image = this.images[index];
      if (!image.file) return;

      image.uploading = true;

      try {
        const formData = new FormData();
        formData.append('token', this.token);
        formData.append('index', index);
        formData.append('image', image.file);

        await this.$root.api.post('/supplier-evidences/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        image.uploaded = true;
      } catch (err) {
        console.error('Error uploading image', err);
        alert(this.$t('evidences.uploadError'));
        this.removeImage(index);
      } finally {
        image.uploading = false;
      }
    },

    removeImage(index) {
      this.$set(this.images, index, {
        file: null,
        preview: null,
        uploading: false,
        uploaded: false
      });
    },

    loadExistingEvidences(evidenceFiles) {
      const unmatched = [];

      // Mapear cada evidencia al slot correspondiente según su nombre
      evidenceFiles.forEach(evidence => {
        const slotIndex = this.evidencesNameImages.findIndex(
          slot => slot.name === evidence.name
        );
        if (slotIndex !== -1) {
          this.$set(this.images, slotIndex, {
            file: null,
            preview: evidence.url,
            uploading: false,
            uploaded: true
          });
        } else {
          unmatched.push(evidence);
        }
      });

      // Si el nombre no coincide con ningún slot (ej. quedó guardado como "blob"),
      // se ubica en el primer cuadro vacío para evitar que se vea como no cargado
      // y se vuelva a subir un duplicado
      unmatched.forEach(evidence => {
        const emptyIndex = this.images.findIndex(img => !img.uploaded);
        if (emptyIndex !== -1) {
          this.$set(this.images, emptyIndex, {
            file: null,
            preview: evidence.url,
            uploading: false,
            uploaded: true
          });
        }
      });
    },

    async submitEvidences() {
      if (!this.canSubmit) return;

      this.submitting = true;

      try {
        // Subir todas las imágenes
        for (let index = 0; index < this.images.length; index++) {
          const image = this.images[index];
          if (!image.file || image.uploaded) continue;

          image.uploading = true;

          const formData = new FormData();
          formData.append('token', this.token);
          formData.append('index', this.evidencesNameImages[index].name);
          formData.append('image', image.file);

          await this.$root.api.post('/supplier-evidences/files', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          image.uploading = false;
          image.uploaded = true;
        }

        // Confirmar evidencias
        await this.$root.api.post('/supplier-evidences/confirm', {
          token: this.token
        });

        // Bloquear edición después de confirmar
        this.hasExistingEvidences = true;

      } catch (err) {
        console.error('Error submitting evidences', err);
        alert(this.$t('evidences.submitError'));
        // Resetear estado de uploading en caso de error
        this.images.forEach(img => { img.uploading = false; });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.evidences-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

/* Navbar */
.evidences-navbar {
  background: linear-gradient(135deg, #021d3e 0%, #0a3d62 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 20px rgba(2, 29, 62, 0.3);
}

.navbar-logo {
  max-height: 44px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}

/* Container */
.evidences-container {
  padding: 24px 16px;
}

/* Loading & Error States */
.loading-state,
.expired-state,
.error-state {
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 20px;
}

.loading-state p {
  margin-top: 16px;
  color: #64748b;
  font-weight: 500;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner.small {
  width: 28px;
  height: 28px;
  border-width: 3px;
  border-top-color: #3b82f6;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.expired-state {
  color: #64748b;
}

.expired-icon {
  font-size: 72px;
  margin-bottom: 20px;
  color: #94a3b8;
}

.error-icon {
  font-size: 72px;
  margin-bottom: 20px;
  color: #ef4444;
}

.hint-icon {
  font-size: 16px;
  vertical-align: middle;
  margin-right: 4px;
}

.expired-state h2,
.error-state h2 {
  color: #ef4444;
  margin-bottom: 12px;
  font-size: 24px;
  font-weight: 700;
}

.expired-hint {
  color: #94a3b8;
  font-size: 15px;
  margin-top: 16px;
}

/* Service Content */
.service-content {
  max-width: 960px;
  margin: 0 auto;
}

@media (max-width: 767px) {
  .service-content {
    max-width: 500px;
  }
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
}

.service-header h1 {
  font-size: 24px;
  margin: 0;
  color: #1e293b;
  font-weight: 700;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon {
  font-size: 28px;
  color: #3b82f6;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.status-badge.noProvided {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.status-badge.provided {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
}

.status-badge.pending {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #3730a3;
}

/* Info Card */
.info-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.info-row {
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s ease;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row.highlight {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  margin: 0 -20px;
  padding: 14px 20px;
  border-left: 3px solid #3b82f6;
}

.info-row .label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
  font-weight: 600;
}

.label-icon {
  font-size: 14px;
  color: #3b82f6;
}

.info-row .value {
  font-size: 15px;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.4;
}

.info-row .value.small {
  font-size: 13px;
  font-weight: 500;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
}

.info-item .label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
  font-weight: 600;
}

.info-item .value {
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
}

/* Warning Notice */
.warning-notice {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #f59e0b;
  border-left: 4px solid #f59e0b;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 0px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #92400e;
  line-height: 1.6;
  box-shadow: 0 2px 12px rgba(245, 158, 11, 0.15);
}

.warning-icon {
  font-size: 24px;
  color: #f59e0b;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-notice strong {
  color: #b45309;
}

/* Upload Section */
.evidences-upload {
  margin-top: 0;
}

@media (max-width: 767px) {
  .evidences-upload {
    margin-top: 24px;
  }

  .service-info {
    margin-top: 0;
  }
}

.evidences-upload h2 {
  font-size: 20px;
  margin: 0 0 8px 0;
  color: #1e293b;
  font-weight: 700;
}

.upload-hint {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px 0;
}

.images-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.image-upload-card {
  aspect-ratio: 1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.image-upload-card:hover:not(.has-image):not(.readonly) {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
}

.image-upload-card.has-image {
  border-style: solid;
  border-color: #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  color: #64748b;
  padding: 12px;
  transition: all 0.3s ease;
}

.camera-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.6;
  color: #64748b;
  transition: all 0.3s ease;
}

.image-upload-card:hover:not(.has-image):not(.readonly) .camera-icon {
  transform: scale(1.1);
  opacity: 1;
}

.upload-placeholder span {
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
  font-weight: 500;
}

.image-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-upload-card:hover .image-preview img {
  transform: scale(1.02);
}

.remove-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  transition: all 0.2s ease;
  opacity: 0;
}

.remove-btn .material-icons {
  font-size: 18px;
}

.image-upload-card:hover .remove-btn {
  opacity: 1;
}

.remove-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.5);
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.uploaded-check {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  animation: checkPop 0.3s ease;
}

.uploaded-check .material-icons {
  font-size: 16px;
}

@keyframes checkPop {
  0% { transform: scale(0); }
  70% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Submit Section */
.submit-section {
  margin-top: 28px;
  padding-bottom: 32px;
}

.submit-btn {
  width: 100%;
  padding: 18px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-icon {
  font-size: 20px;
}

.btn-icon.spinning {
  animation: spin 1s linear infinite;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  cursor: not-allowed;
  box-shadow: none;
}

.upload-progress {
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.progress-icon {
  font-size: 18px;
  color: #3b82f6;
}

/* Readonly state for existing evidences */
.image-upload-card.readonly {
  border-color: #10b981;
  border-style: solid;
  cursor: default;
}

.image-upload-card.readonly:hover {
  transform: none;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
}

.upload-placeholder.readonly {
  cursor: default;
  opacity: 0.5;
}

.evidence-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%);
  color: white;
  padding: 10px 8px;
  font-size: 11px;
  text-align: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Success notice */
.success-notice {
  margin-top: 0;
  margin-bottom: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-radius: 16px;
  text-align: center;
  color: #065f46;
  border: 1px solid #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.2);
}

.success-icon {
  font-size: 40px;
  color: #10b981;
  display: block;
  margin-bottom: 8px;
}

.success-notice p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .service-header h1 {
    font-size: 20px;
  }

  .info-card {
    padding: 16px;
  }

  .info-row.highlight {
    margin: 0 -16px;
    padding: 12px 16px;
  }

  .images-grid {
    gap: 12px;
  }

  .image-upload-card:hover .remove-btn {
    opacity: 1;
  }
}
</style>
